import type { ShadowDay, TimeDay } from "../api/types";
import {
  MAX_SHADOW_SPAN_DAYS,
  branchRulePath,
  chantierKey,
  type ProjectOf,
} from "./chantiers";

/** Revue de la semaine avant soumission, pour soi seul : ce qui bloque la
 * soumission (chevauchements), et ce que les observations du poste invitent à
 * vérifier. Rien ici ne crée ni ne modifie de temps (DT Corroboration § 3). */

/** La lecture shadow couvre 31 jours au plus, bornes incluses : une période
 * plus longue n'est pas demandée. */
export function shadowSpanFits(from: string, to: string): boolean {
  const day = (k: string) => {
    const [y, m, d] = k.split("-").map(Number);
    return Date.UTC(y!, m! - 1, d!);
  };
  return (day(to) - day(from)) / 86_400_000 <= MAX_SHADOW_SPAN_DAYS;
}

export interface ReviewOverlap {
  date: string;
  startMinute: number;
  endMinute: number;
  codes: string[];
}

/** Chevauchements d'activités de la période, du calcul serveur des jours : le
 * même que celui qui refuse la soumission. */
export function weekOverlaps(
  days: Iterable<TimeDay>,
  from: string,
  to: string,
): ReviewOverlap[] {
  const out: ReviewOverlap[] = [];
  for (const d of days) {
    if (d.date < from || d.date > to) continue;
    for (const o of d.overlaps ?? []) {
      out.push({
        date: d.date,
        startMinute: o.start_minute,
        endMinute: o.end_minute,
        codes: o.codes,
      });
    }
  }
  return out.sort(
    (a, b) => a.date.localeCompare(b.date) || a.startMinute - b.startMinute,
  );
}

export interface ReviewBranch {
  repo: string;
  branch: string;
  seconds: number;
}

function byTime(a: ReviewBranch, b: ReviewBranch): number {
  return (
    b.seconds - a.seconds ||
    a.repo.localeCompare(b.repo) ||
    a.branch.localeCompare(b.branch)
  );
}

/** Temps git sans activité de poste de la période, par dépôt et branche, du
 * plus long au plus court. null quand TAWA ne le calcule pas encore : inconnu,
 * ce qui n'est pas « aucun ». */
export function gitOnlyBranches(days: ShadowDay[]): ReviewBranch[] | null {
  if (!days.some((d) => d.git_only_seconds !== undefined)) return null;
  const acc = new Map<string, ReviewBranch>();
  for (const d of days) {
    for (const r of d.repos) {
      for (const b of r.branches ?? []) {
        const seconds = b.git_only_seconds ?? 0;
        if (seconds <= 0) continue;
        const key = `${r.repo}\n${b.branch}`;
        const cur = acc.get(key);
        if (cur) cur.seconds += seconds;
        else acc.set(key, { repo: r.repo, branch: b.branch, seconds });
      }
    }
  }
  return [...acc.values()].sort(byTime);
}

export interface AttachTarget extends ReviewBranch {
  /** Création de la règle préremplie. */
  path: string;
}

/** Dépôts et branches observés sans règle qui les rattache à un projet. Le
 * tronc, la branche vide et les worktrees d'agent se rattachent par le dépôt
 * entier (branch vide) ; une branche de chantier, par son motif exact. */
export function attachTargets(
  days: ShadowDay[],
  projectOf: ProjectOf,
  workerUrl: string,
): AttachTarget[] {
  const acc = new Map<string, AttachTarget>();
  for (const d of days) {
    for (const r of d.repos) {
      const branches = r.branches?.length
        ? r.branches
        : [{ branch: "", server_seconds: r.server_seconds }];
      for (const b of branches) {
        if (b.server_seconds <= 0 || projectOf(r.repo, b.branch)) continue;
        const branch = chantierKey(b.branch) === null ? "" : b.branch;
        const key = `${r.repo}\n${branch}`;
        const cur = acc.get(key);
        if (cur) cur.seconds += b.server_seconds;
        else
          acc.set(key, {
            repo: r.repo,
            branch,
            seconds: b.server_seconds,
            path: branchRulePath(r.repo, branch, workerUrl),
          });
      }
    }
  }
  return [...acc.values()].sort(byTime);
}
